import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Button,
  Input,
  Message,
  useToaster,
  SelectPicker,
  Stack,
  List,
  IconButton,
  Loader,
  InputNumber,
  Accordion
} from 'rsuite';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getAccessByGroup } from '@/services/access.service';
import { clearGroupAccess, updateGroupZonePriorities } from '@/services/accessProfile.service';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { Trash } from '@rsuite/icons';
import { RiUserLocationLine } from 'react-icons/ri';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const titleStyle = {
  fontSize: '1.1em',
  fontWeight: 600,
  color: '#333',
  margin: 0
};

const subTitleStyle = {
  fontSize: '1em',
  fontWeight: 500,
  color: '#666',
  marginLeft: 30
};

interface UpdatePriorityDrawerProps {
  open: boolean;
  onClose: () => void;
  data: any;
}

const UpdatePriorityDrawer: React.FC<UpdatePriorityDrawerProps> = ({ open, onClose, data }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const [formValue, setFormValue] = useState({
    name: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  // Get floors from store
  const floorsData = useAppSelector((state: any) => state.floor?.data || []);
  // Get zones from store
  const zones = useAppSelector((state: any) => state.zone?.data || [])
    ?.map(item => {
      return {
        ...item,
        value: item._id,
        label: `${item.floor?.building?.address} / ${item.floor?.name} / ${item?.name}`
      };
    })
    ?.sort((a, b) => a.floor.floor_num - b.floor.floor_num);
  // Get access data
  const groupAccessData = useAppSelector((state: any) => state.access.groupAccessData);

  // Process floors only once per render
  // const floors = React.useMemo(() => {
  //   return floorsData.map(item => ({
  //     ...item,
  //     value: item._id,
  //     label: item.name,
  //     sortOrder:
  //       item.name.toLowerCase().includes('parking') ||
  //       item.name.toLowerCase().includes('basement') ||
  //       item.name.startsWith('-')
  //         ? -parseInt(item.name.match(/\d+/)?.[0] || '0')
  //         : parseInt(item.name.match(/\d+/)?.[0] || '0')
  //   }));
  // }, [floorsData]);

  // // Process zones only once per render
  // const zones = React.useMemo(() => {
  //   const mappedZones = zonesData.map(item => {
  //     const floorObj = floors.find(floor => floor.value === item.floor);
  //     return {
  //       ...item,
  //       value: item._id,
  //       label: `${floorObj?.building?.address || 'building'} / ${
  //         floorObj?.label || t('Unknown Floor')
  //       } / ${item.name}`,
  //       floor: item.floor,
  //       sortOrder: floorObj?.sortOrder || 0
  //     };
  //   });

  //   return mappedZones.sort((a, b) => a.sortOrder - b.sortOrder);
  // }, [zonesData, floors]);

  const daysOfWeek = [
    { label: t('Monday'), value: 1 },
    { label: t('Tuesday'), value: 2 },
    { label: t('Wednesday'), value: 3 },
    { label: t('Thursday'), value: 4 },
    { label: t('Friday'), value: 5 },
    { label: t('Saturday'), value: 6 },
    { label: t('Sunday'), value: 7 }
  ];

  const [localAccessList, setLocalAccessList] = useState([
    {
      zone: null,
      data: [
        {
          dayOfWeek: null,
          priority: null
        }
      ]
    }
  ]);

  const handleClose = () => {
    dispatch(clearGroupAccess());
    onClose();
  };

  // Effect for initial data loading
  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          dispatch(getFloors()),
          dispatch(getZones()),
          dispatch(getAccessByGroup(data._id))
        ]);
      } catch (error) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t('Failed to initialize form data')}
          </Message>,
          { placement: 'topCenter' }
        );
      } finally {
        setLoading(false);
      }
    };

    if (open && data) {
      initData();
      setFormValue({
        name: data.name || '',
        description: data.description || ''
      });
      setLocalAccessList([
        {
          zone: null,
          data: [
            {
              dayOfWeek: null,
              priority: null
            }
          ]
        }
      ]);
    }

    return () => {
      dispatch(clearGroupAccess());
    };
  }, [open, data, dispatch]);

  // Separate effect for processing groupAccessData
  // Important: only depends on groupAccessData and a stable reference to zones
  useEffect(() => {
    if (groupAccessData?.length > 0 && zones.length > 0) {
      // Create a map of zoneId to zone object for efficient lookup
      const zoneMap = {};
      zones.forEach(zone => {
        zoneMap[zone._id] = zone;
      });

      // Group the data by zone
      const groupedByZone = groupAccessData.reduce((acc, item) => {
        if (!acc[item.zone]) {
          acc[item.zone] = [];
        }
        acc[item.zone].push({
          dayOfWeek: item.dayOfWeek,
          priority: item.priority
        });
        return acc;
      }, {});

      // Format the data for localAccessList with zone objects for sorting
      const formattedList = Object.entries(groupedByZone).map(([zoneId, data]) => {
        // Find the zone's sortOrder from our map
        const zoneObj = zoneMap[zoneId];
        return {
          zone: zoneId,
          sortOrder: zoneObj?.sortOrder || 0,
          data: data as { dayOfWeek: number; priority: number }[]
        };
      });

      // Sort the list by zone sortOrder before setting to state
      const sortedList = formattedList.sort((a, b) => a.sortOrder - b.sortOrder);

      // Remove the sortOrder property before setting state
      const cleanedList = sortedList.map(({ zone, data }) => ({ zone, data }));

      setLocalAccessList(cleanedList);
    }
  }, [groupAccessData]); // Only depend on groupAccessData, zones reference is stable now

  const handleAddZoneRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        zone: null,
        data: [
          {
            dayOfWeek: null,
            priority: null
          }
        ]
      }
    ]);
  };

  const handleAddRow = (indexRow: number) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        return {
          ...row,
          data: [
            ...row.data,
            {
              dayOfWeek: null,
              priority: null
            }
          ]
        };
      })
    );
  };

  const handleChange = (indexRow: number, key: string, value: any, index: number | null = null) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;

        if (key === 'zone') {
          return {
            ...row,
            zone: value,
            data: daysOfWeek.map(day => ({
              dayOfWeek: day.value,
              priority: 1
            }))
          };
        }

        if (index === null) {
          return { ...row, [key]: value };
        } else {
          const updatedData = row.data.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [key]: value } : item
          );
          return { ...row, data: updatedData };
        }
      })
    );
  };

  const handleDeleteZoneRow = (indexRow: number) => {
    setLocalAccessList(prevList => prevList.filter((_, rowIndex) => rowIndex !== indexRow));
  };

  const handleRemoveRow = (indexRow: number, index: number) => {
    if (localAccessList[indexRow].data.length === 1) {
      return;
    }

    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        const updatedData = row.data.filter((_, itemIndex) => itemIndex !== index);
        return { ...row, data: updatedData };
      })
    );
  };

  const handleSubmit = async () => {
    try {
      if (!formValue.name || localAccessList.some(item => !item.zone)) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            Please fill in all required fields
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      const invalidZones = localAccessList.some(
        item => !item.data || item.data.some(d => !d.dayOfWeek || d.priority === null)
      );
      if (invalidZones) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t('Please fill in all day and priority values')}
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      setIsSaving(true);

      const updateData = {
        group: {
          name: formValue.name,
          description: formValue.description
        },
        priorityConfigs: localAccessList.flatMap(({ zone, data }) =>
          data.map(({ dayOfWeek, priority }) => ({
            zone,
            dayOfWeek: parseInt(dayOfWeek),
            priority: parseInt(priority)
          }))
        )
      };

      await dispatch(updateGroupZonePriorities(data._id, updateData));

      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Profile updated successfully')}
        </Message>,
        { placement: 'topCenter' }
      );

      onClose();
    } catch (error: any) {
      console.error('Update error:', error);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error updating profile')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <Loader center content="Loading..." />;
  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={handleClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Update Access Profile with Priority')}</Drawer.Title>
        <Drawer.Actions>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            loading={isSaving}
            disabled={isSaving}
          >
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle" disabled={isSaving}>
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Name *')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter profile name" required />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Description')}</Form.ControlLabel>
            <Form.Control
              name="description"
              placeholder="Enter description"
              accepter={Input as any}
            />
          </Form.Group>

          <Stack
            style={{
              marginTop: 50,
              marginBottom: 20,
              marginLeft: 10
            }}
            spacing={10}
            alignItems="center"
          >
            <RiUserLocationLine size={20} />
            <h6 style={titleStyle}>{t('Manage accesses with priority')}</h6>
          </Stack>

          <div className="ml-10">
            <Accordion defaultActiveKey={0}>
              {localAccessList.map((row, indexRow) => (
                <Accordion.Panel
                  key={indexRow}
                  header={
                    <Stack spacing={10} alignItems="flex-start">
                      {row.zone ? (
                        <span style={subTitleStyle}>
                          {zones.find(z => z.value === row.zone)?.label || 'No zone selected'}
                        </span>
                      ) : (
                        <span style={subTitleStyle}>{t('New Zone Access')}</span>
                      )}
                    </Stack>
                  }
                  defaultExpanded
                >
                  <Stack justifyContent="space-between">
                    <div style={{ textAlign: 'left', marginLeft: '28px' }}>
                      <div style={slimText}>Zone *</div>
                      <SelectPicker
                        data={zones}
                        value={row.zone}
                        onChange={value => handleChange(indexRow, 'zone', value)}
                        style={{ width: 250 }}
                        placement="auto"
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== indexRow)
                          .map(item => item.zone)
                          .filter(Boolean)}
                        cleanable={false}
                        placeholder={t('Select Zone')}
                      />
                    </div>
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<Trash color="red" />}
                      onClick={() => handleDeleteZoneRow(indexRow)}
                      disabled={localAccessList.length === 1}
                    />
                  </Stack>

                  <List style={{ marginLeft: 50, marginTop: 20 }}>
                    {row.data
                      // .sort((a, b) => (a.dayOfWeek || 0) - (b.dayOfWeek || 0))
                      .map((item, index) => (
                        <List.Item key={index}>
                          <Stack spacing={20} wrap>
                            <div style={{ textAlign: 'left' }}>
                              <div style={slimText}>{t('Day of week *')}</div>
                              <SelectPicker
                                data={daysOfWeek}
                                value={item.dayOfWeek}
                                onChange={value =>
                                  handleChange(indexRow, 'dayOfWeek', value, index)
                                }
                                style={{ width: 250 }}
                                placement="auto"
                                disabledItemValues={row.data
                                  .filter((_, i) => i !== index)
                                  .map(item => item.dayOfWeek)
                                  .filter(Boolean)}
                                placeholder={t('Select Day')}
                              />
                            </div>

                            <div style={{ textAlign: 'left' }}>
                              <div style={slimText}>{t('Priority *')}</div>
                              <InputNumber
                                value={item.priority}
                                onChange={value => handleChange(indexRow, 'priority', value, index)}
                                style={{ width: 100 }}
                                min={1}
                              />
                            </div>

                            <Stack spacing={5} alignItems="flex-end">
                              <IconButton
                                onClick={() => handleRemoveRow(indexRow, index)}
                                icon={<MinusIcon />}
                                size="xs"
                                disabled={row.data.length === 1}
                              />
                              {index === row.data.length - 1 && (
                                <IconButton
                                  onClick={() => handleAddRow(indexRow)}
                                  icon={<PlusIcon />}
                                  size="xs"
                                />
                              )}
                            </Stack>
                          </Stack>
                        </List.Item>
                      ))}
                  </List>
                </Accordion.Panel>
              ))}
            </Accordion>

            <IconButton
              onClick={handleAddZoneRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default UpdatePriorityDrawer;
